import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/react-hooks';
import { clientErrorHandler } from '@sm/webassets';
import { useLocation } from 'react-router-dom';
import { SearchContext } from '../../../../components/SearchContext';
import appsAppListings from './AppListings.graphql';
import AppListingSkeleton from './AppListingSkeleton';
import { POPULAR } from '~app/pages/AppsDirectory/components/SearchContext/constants';

const initialState = {
  search: '',
  categories: [],
  featured: false,
  sortBy: POPULAR.value,
  page: 1,
  pageSize: 16,
};

const flattenCategories = params => {
  return params.categories.length ? { ...params, categories: [params.categories.toString()] } : params;
};

export const AppListingContext = createContext({
  appListings: [],
  isInitialSearch: false,
  pageInfo: {},
});

export const AppListingProvider = ({ children }) => {
  const { state } = useLocation();

  const { search, filters, sortBy, pageNum, resetSearch } = useContext(SearchContext);
  const [appListings, setAppListings] = useState([]);
  const [isInitialSearch, setInitialSearch] = useState(true);
  const [pageInfo, setPageInfo] = useState({});
  const [paramsChanged, setParamsChanged] = useState(false);
  const [variables, setVariables] = useState(initialState);

  const [getAppListings, { loading: isLoading, error, data }] = useLazyQuery(appsAppListings);

  useEffect(() => {
    if (data?.fetchPublishedApplicationListings?.data) {
      const { data: listings, ...paging } = data.fetchPublishedApplicationListings;
      setPageInfo(paging);
      setAppListings(listings);
    }
  }, [data]);

  useEffect(() => {
    if (state) {
      const { moreParams = {} } = state;
      if (Object.keys(moreParams).length) {
        setParamsChanged(true);
        setVariables(prevVariables => ({
          ...prevVariables,
          ...moreParams,
        }));
      }
    }
  }, [state]);

  useEffect(() => {
    if (error) {
      clientErrorHandler.logError(error);
    }
  }, [error]);

  useEffect(() => {
    if (paramsChanged) {
      const params = flattenCategories(variables);
      setInitialSearch(JSON.stringify(params) === JSON.stringify(initialState));
      getAppListings({
        variables: params,
      });
    }
  }, [getAppListings, variables, paramsChanged]);

  useEffect(() => {
    setVariables(prevVariables => {
      if (prevVariables.search !== search) {
        setParamsChanged(true);
        return { ...prevVariables, search };
      }
      return prevVariables;
    });
  }, [search]);

  useEffect(() => {
    setVariables(prevVariables => {
      if (prevVariables.categories !== filters) {
        setParamsChanged(true);
        return { ...prevVariables, categories: filters };
      }
      return prevVariables;
    });
  }, [filters]);

  useEffect(() => {
    setVariables(prevVariables => {
      if (prevVariables.sortBy !== sortBy) {
        setParamsChanged(true);
        return { ...prevVariables, sortBy };
      }
      return prevVariables;
    });
  }, [sortBy]);

  useEffect(() => {
    setVariables(prevVariables => {
      const nextPageNum = pageNum + 1;
      if (prevVariables.page !== nextPageNum) {
        setParamsChanged(true);
        return { ...prevVariables, page: nextPageNum };
      }
      return prevVariables;
    });
  }, [pageNum]);

  const clearSearch = () => {
    resetSearch();
    setVariables(initialState);
  };

  if (isLoading) {
    return <AppListingSkeleton data-testid="AppListingContext__Skeleton" />;
  }

  return (
    <AppListingContext.Provider value={{ appListings, isInitialSearch, pageInfo, isLoading, clearSearch }}>
      {children}
    </AppListingContext.Provider>
  );
};

AppListingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppListingContext;
